import { useEffect, useRef, type ReactNode } from 'react';
import { cx, getOptionId } from './primitiveUtils';

/**
 * The option list inside a Combobox or SelectMenu popover.
 *
 * Focus never enters it: the input / trigger keeps focus and points at the active
 * option with `aria-activedescendant` (ids from `getOptionId`). So options swallow
 * mousedown (the input must not blur) and select on click.
 *
 * Status text (loading, no results) is NOT rendered in here, because a listbox may
 * only own options; the owning control renders it beside the list.
 */

export interface ListboxOptionState {
  active: boolean;
  selected: boolean;
  disabled: boolean;
  index: number;
}

export interface ListboxProps<T> {
  /** Element id; the controlling input's `aria-controls` points here. */
  id: string;
  options: readonly T[];
  getOptionKey: (option: T, index: number) => string;
  renderOption: (option: T, state: ListboxOptionState) => ReactNode;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (option: T, index: number) => void;
  isSelected?: (option: T, index: number) => boolean;
  isDisabled?: (option: T, index: number) => boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  multiselectable?: boolean;
  busy?: boolean;
  /** Check mark at the inline end of selected options. Default true. */
  showCheck?: boolean;
  /** sm: 0.875rem text / 1rem icons. md (default): 0.9375rem / 1.125rem. */
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE = {
  sm: { row: 'min-h-[2.75rem] gap-2.5 px-3 py-2 text-[0.875rem]', check: 'text-[1rem]' },
  md: { row: 'min-h-[2.75rem] gap-3 px-3.5 py-2.5 text-[0.9375rem]', check: 'text-[1.125rem]' },
} as const;

export default function Listbox<T>({
  id,
  options,
  getOptionKey,
  renderOption,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  isSelected,
  isDisabled,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  multiselectable,
  busy,
  showCheck = true,
  size = 'md',
  className = '',
}: ListboxProps<T>) {
  // Keep the keyboard-active option in view, but not for hover: scrolling the item
  // under a resting pointer would keep re-hovering the next one and run away.
  const pointerMovedRef = useRef(false);
  // Chrome fires a synthetic mousemove after the list scrolls; only a real move counts.
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (activeIndex < 0) return;
    if (pointerMovedRef.current) {
      pointerMovedRef.current = false;
      return;
    }
    const el = document.getElementById(getOptionId(id, activeIndex));
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, id]);

  const s = SIZE[size];

  return (
    <ul
      id={id}
      role="listbox"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-multiselectable={multiselectable || undefined}
      aria-busy={busy || undefined}
      className={cx('py-1', className)}
    >
      {options.map((option, index) => {
        const selected = isSelected?.(option, index) ?? false;
        const disabled = isDisabled?.(option, index) ?? false;
        const active = index === activeIndex;
        return (
          <li
            key={getOptionKey(option, index)}
            id={getOptionId(id, index)}
            role="option"
            aria-selected={selected}
            aria-disabled={disabled || undefined}
            data-active={active ? '' : undefined}
            onMouseDown={(event) => event.preventDefault()}
            onMouseMove={(event) => {
              const last = lastPointRef.current;
              if (last && last.x === event.clientX && last.y === event.clientY) return;
              lastPointRef.current = { x: event.clientX, y: event.clientY };
              if (active || disabled) return;
              pointerMovedRef.current = true;
              onActiveIndexChange(index);
            }}
            onClick={() => {
              if (!disabled) onSelect(option, index);
            }}
            className={cx(
              'flex cursor-pointer select-none items-center text-start leading-snug transition-colors duration-100',
              s.row,
              active && 'bg-[var(--pv-color-surface-sunken)]',
              selected ? 'text-[var(--pv-color-primary)]' : 'text-[var(--pv-color-text)]',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <span className="min-w-0 flex-1">
              {renderOption(option, { active, selected, disabled, index })}
            </span>
            {showCheck && selected && (
              <i
                className={cx('ri-check-line shrink-0 leading-none text-[var(--pv-color-primary)]', s.check)}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
